import React, { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import { X } from "lucide-react";
import { StepLineIndicator } from "../ui/step-indicator";
import { SlideUpTransition } from "./mobile/mobile-bottom-sheet";

import useApi from "../../hooks/use-api";
import buildApiRequestConfig from "../../api/url-builder";
import routes from "../../api/routes";
import { useToast } from "../../providers/toaster-provider";

export interface BasiqImportPopupProps {
  open: boolean;
  onClose: () => void;
  onComplete: (data: any) => void;
  isMobile?: boolean;
}

const BasiqImportPopup: React.FC<BasiqImportPopupProps> = ({
  open,
  onClose,
  onComplete,
  isMobile = false,
}) => {
  const { showError, showSuccess } = useToast();

  const [step, setStep] = React.useState(0);
  const [basiqEmail, setBasiqEmail] = React.useState<string>("");
  const [basiqUserId, setBasiqUserId] = React.useState<string>("");
  const [basiqTransactionDateRange, setBasiqTransactionDateRange] =
    React.useState<{ from: string; to: string }>({
      from: new Date(new Date().getFullYear(), 0, 1)
        .toISOString()
        .split("T")[0],
      to: new Date().toISOString().split("T")[0],
    });
  const [pulledTransactions, setPulledTransactions] = React.useState<any[]>([]);

  const [createBasiqUser, setCreateBasiqUser] = useApi();
  const [pullTransactions, setPullTransactions] = useApi();
  const [userIdError, setUserIdError] = React.useState<string>("");
  const prevCreateUserRef = React.useRef<typeof createBasiqUser>(null);
  const prevPullTransRef = React.useRef<typeof pullTransactions>(null);

  // Reset state when modal opens
  React.useEffect(() => {
    if (!open) {
      setStep(0);
      setBasiqEmail("");
      setBasiqUserId("");
      setUserIdError("");
      setPulledTransactions([]);
      setBasiqTransactionDateRange({
        from: new Date(new Date().getFullYear(), 0, 1)
          .toISOString()
          .split("T")[0],
        to: new Date().toISOString().split("T")[0],
      });
    }
  }, [open]);

  // Handle create basiq user response
  React.useEffect(() => {
    if (createBasiqUser.data !== prevCreateUserRef.current?.data) {
      prevCreateUserRef.current = createBasiqUser;
      
      if (createBasiqUser.data && !createBasiqUser.isLoading && createBasiqUser.data.userId) {
        setBasiqUserId(createBasiqUser.data.userId);
        showSuccess("Basiq user created successfully");
        setStep(1);
      }
    }
    if (createBasiqUser.error && !createBasiqUser.isLoading && createBasiqUser.error !== prevCreateUserRef.current?.error) {
      prevCreateUserRef.current = createBasiqUser;
      showError("Failed to create Basiq user");
    }
  }, [createBasiqUser, showError, showSuccess]);

  // Handle pull transactions response
  React.useEffect(() => {
    if (pullTransactions.data !== prevPullTransRef.current?.data) {
      prevPullTransRef.current = pullTransactions;
      
      if (pullTransactions.data && !pullTransactions.isLoading) {
        showSuccess("Transactions pulled successfully");
        // Extract transactions from response (could be array or nested in data)
        const txArray = Array.isArray(pullTransactions.data) 
          ? pullTransactions.data 
          : pullTransactions.data.transactions || [pullTransactions.data];
        setPulledTransactions(txArray);
        setStep(2);
        // Don't call onComplete here - wait for user to confirm in step 3
      }
    }
    if (pullTransactions.error && !pullTransactions.isLoading && pullTransactions.error !== prevPullTransRef.current?.error) {
      prevPullTransRef.current = pullTransactions;
      showError("Failed to pull transactions");
    }
  }, [pullTransactions, showError, showSuccess]);

  const handleCreateBasiqUser = () => {
    if (!basiqEmail) {
      showError("Please enter an email");
      return;
    }
    const { url, method } = buildApiRequestConfig(routes.basiq.createUser, []);
    setCreateBasiqUser({ url, method, body: { email: basiqEmail } });
  };

  const handleOpenConsentUI = () => {
    if (!basiqUserId) {
      setUserIdError("Please enter a Basiq User ID first");
      return;
    }
    setUserIdError("");
    const { url } = buildApiRequestConfig(routes.basiq.connectUser, [
      { param: "basiqUserId", value: basiqUserId },
    ]);
    window.open(url, "_blank");
  };

  const handlePullTransactions = () => {
    if (!basiqUserId) {
      setUserIdError("Please enter a Basiq User ID first");
      return;
    }
    setUserIdError("");
    const { url, method } = buildApiRequestConfig(routes.basiq.getTransactions);
    setPullTransactions({
      url,
      method,
      body: {
        from: basiqTransactionDateRange.from,
        to: basiqTransactionDateRange.to,
        userId: basiqUserId,
      },
    });
  };

  const handleNextStep = () => {
    if (step < 3) {
      setStep(step + 1);
    }
  };

  const handlePrevStep = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-AU", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const formatAmount = (amount: string | number): string => {
    try {
      const num = typeof amount === "string" ? parseFloat(amount) : amount;
      return new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
      }).format(num);
    } catch {
      return String(amount);
    }
  };

  const handleConfirmImport = () => {
    // Call onComplete only when user confirms the import
    if (onComplete) {
      onComplete(pullTransactions.data);
    }
    // Close modal after confirming
    onClose();
  };

  // Prepare transaction columns for virtualized table
  const transactionColumns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "transactionDate",
        header: "Date",
        size: 120,
        Cell: ({ cell }) => formatDate(cell.getValue<string>()),
      },
      {
        accessorKey: "amount",
        header: "Amount",
        size: 120,
        Cell: ({ cell }) => formatAmount(cell.getValue<string | number>()),
      },
      {
        accessorKey: "direction",
        header: "Direction",
        size: 100,
        Cell: ({ cell }) => (
          <Box
            sx={{
              display: "inline-block",
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              backgroundColor:
                cell.getValue() === "debit" ? "#ffebee" : "#e8f5e9",
              color: cell.getValue() === "debit" ? "#c62828" : "#2e7d32",
              fontSize: 12,
              fontWeight: 600,
              textTransform: "capitalize",
            }}
          >
            {String(cell.getValue())}
          </Box>
        ),
      },
      {
        accessorKey: "description",
        header: "Description",
        size: 250,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 100,
        Cell: ({ cell }) => (
          <Box
            sx={{
              display: "inline-block",
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: "#e3f2fd",
              color: "#0d47a1",
              fontSize: 12,
              fontWeight: 600,
              textTransform: "capitalize",
            }}
          >
            {String(cell.getValue())}
          </Box>
        ),
      },
    ],
    []
  );

  return (
    <Dialog
      open={open}
      maxWidth={isMobile ? false : "xl"}
      fullWidth={isMobile}
      {...(isMobile ? { TransitionComponent: SlideUpTransition } : {})}
      sx={isMobile ? { '& .MuiDialog-container': { alignItems: 'flex-end' } } : undefined}
      PaperProps={{
        sx: isMobile
          ? { m: 0, width: '100%', maxWidth: '100%', height: '90vh', maxHeight: '90vh', borderTopLeftRadius: 16, borderTopRightRadius: 16, borderBottomLeftRadius: 0, borderBottomRightRadius: 0, p: 0, display: 'flex', flexDirection: 'column' }
          : { borderRadius: 4, p: 0, height: 750, display: "flex", flexDirection: "column" },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          pt: 3,
          pb: 0,
          px: isMobile ? 2 : 4,
          width: isMobile ? '100%' : 1000,
          overflowX: "hidden",
          overflowY: "auto",
          position: "relative",
          flex: 1,
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 1,
            "&:hover": {
              backgroundColor: "#e0e0e0",
            },
            width: 32,
            height: 32,
          }}
        >
          <X size={18} />
        </IconButton>

        <Typography
          textAlign="center"
          fontSize={14}
          fontWeight={600}
          mb={3}
          sx={{ color: "var(--color-default-text)" }}
        >
          Import from Bank (Basiq)
        </Typography>

        <Box display="flex" justifyContent="center" mb={3}>
          <StepLineIndicator
            steps={4}
            activeStep={step}
            width={400}
            stepNames={["Create/Enter User", "Connect Bank", "Pull Transactions", "Review Transactions"]}
          />
        </Box>

        {/* Step 0: Create/Enter Basiq User */}
        {step === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography fontSize={13} fontWeight={600} sx={{ mb: 1 }}>
                Step 1: Create or Enter Basiq User ID
              </Typography>
              <Typography fontSize={13} sx={{ mb: 2, color: "var(--color-text-muted)" }}>
                Create a new user by email, or enter an existing Basiq User ID
              </Typography>
            </Box>

            <Box>
              <Typography fontSize={13} fontWeight={500} mb={0.5} color="var(--color-text-muted)">
                Email <span style={{ color: "#ff3b3b" }}>*</span>
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  placeholder="user@example.com"
                  size="small"
                  fullWidth
                  value={basiqEmail}
                  onChange={(e) => setBasiqEmail(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "var(--color-container-background)",
                    },
                  }}
                />
                <Button
                  variant="contained"
                  sx={{
                    color: "#fff",
                    whiteSpace: "nowrap",
                    backgroundColor: "var(--color-primary)",
                    "&:hover": { backgroundColor: "var(--color-primary)" },
                  }}
                  onClick={handleCreateBasiqUser}
                  disabled={createBasiqUser.isLoading}
                >
                  {createBasiqUser.isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Create"
                  )}
                </Button>
              </Box>
            </Box>

            <Box sx={{ textAlign: "center", color: "var(--color-text-muted)", fontSize: 12 }}>
              – or –
            </Box>

            <Box>
              <Typography fontSize={13} fontWeight={500} mb={0.5} color="var(--color-text-muted)">
                Existing Basiq User ID <span style={{ color: "#ff3b3b" }}>*</span>
              </Typography>
              <TextField
                placeholder="e.g. ea3a81 or full UUID"
                size="small"
                fullWidth
                value={basiqUserId}
                onChange={(e) => {
                  setBasiqUserId(e.target.value);
                  setUserIdError("");
                }}
                error={Boolean(userIdError)}
                helperText={userIdError}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "var(--color-container-background)",
                  },
                }}
              />
            </Box>
          </Box>
        )}

        {/* Step 1: Connect Bank */}
        {step === 1 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography fontSize={13} fontWeight={600} sx={{ mb: 1 }}>
                Step 2: Connect Your Bank Account
              </Typography>
              <Typography fontSize={13} sx={{ mb: 2, color: "var(--color-text-muted)" }}>
                Click the button below to open Basiq's consent UI. This will allow you to
                securely connect your bank account.
              </Typography>
            </Box>

            <Box
              sx={{
                backgroundColor: "var(--color-container-background)",
                p: 2,
                borderRadius: 1,
                border: "1px solid #e0e0e0",
              }}
            >
              <Typography fontSize={12} sx={{ color: "var(--color-text-muted)" }}>
                <strong>For testing:</strong> Use Hooli Bank with credentials:
                <br />
                Username: Wentworth-Smith
                <br />
                Password: whistler
              </Typography>
            </Box>

            <Box>
              <Typography fontSize={13} fontWeight={500} mb={0.5} color="var(--color-text-muted)">
                Basiq User ID <span style={{ color: "#ff3b3b" }}>*</span>
              </Typography>
              <TextField
                placeholder="User ID"
                size="small"
                fullWidth
                value={basiqUserId}
                onChange={(e) => {
                  setBasiqUserId(e.target.value);
                  setUserIdError("");
                }}
                error={Boolean(userIdError)}
                helperText={userIdError}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "var(--color-container-background)",
                  },
                }}
              />
            </Box>
          </Box>
        )}

        {/* Step 2: Pull Transactions */}
        {step === 2 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography fontSize={13} fontWeight={600} sx={{ mb: 1 }}>
                Step 3: Pull Transactions
              </Typography>
              <Typography fontSize={13} sx={{ mb: 2, color: "var(--color-text-muted)" }}>
                Select the date range for the transactions you want to import
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Box flex={1}>
                <Typography fontSize={13} fontWeight={500} mb={0.5} color="var(--color-text-muted)">
                  From <span style={{ color: "#ff3b3b" }}>*</span>
                </Typography>
                <TextField
                  type="date"
                  size="small"
                  fullWidth
                  value={basiqTransactionDateRange.from}
                  onChange={(e) =>
                    setBasiqTransactionDateRange({
                      ...basiqTransactionDateRange,
                      from: e.target.value,
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "var(--color-container-background)",
                    },
                  }}
                />
              </Box>
              <Box flex={1}>
                <Typography fontSize={13} fontWeight={500} mb={0.5} color="var(--color-text-muted)">
                  To <span style={{ color: "#ff3b3b" }}>*</span>
                </Typography>
                <TextField
                  type="date"
                  size="small"
                  fullWidth
                  value={basiqTransactionDateRange.to}
                  onChange={(e) =>
                    setBasiqTransactionDateRange({
                      ...basiqTransactionDateRange,
                      to: e.target.value,
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "var(--color-container-background)",
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}

        {/* Step 2: Completion Message */}
        {step === 2 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, textAlign: "center" }}>
            <Typography fontSize={16} fontWeight={600} sx={{ mb: 1 }}>
              ✓ Transactions Pulled
            </Typography>
            <Typography fontSize={13} sx={{ color: "var(--color-text-muted)" }}>
              {pulledTransactions.length} transaction(s) were successfully pulled from your bank
              account.
            </Typography>
          </Box>
        )}

        {/* Step 3: Review Transactions */}
        {step === 3 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, height: "100%" }}>
            <Box>
              <Typography fontSize={13} fontWeight={600} sx={{ mb: 1 }}>
                Review Pulled Transactions
              </Typography>
              <Typography fontSize={13} sx={{ color: "var(--color-text-muted)", mb: 2 }}>
                {pulledTransactions.length} transaction(s) ready for import
              </Typography>
            </Box>

            {pulledTransactions.length === 0 ? (
              <Box
                sx={{
                  border: "1px solid #FFD700",
                  borderRadius: 2,
                  backgroundColor: "rgba(255, 217, 0, 0.13)",
                  color: "#B8860B",
                  padding: 4,
                  textAlign: "center",
                  minHeight: 75,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
                  No transactions were imported
                </Typography>
              </Box>
            ) : (
              <Box sx={{ flex: 1, overflow: "hidden" }}>
                <MaterialReactTable
                  columns={transactionColumns}
                  data={pulledTransactions}
                  enablePagination={false}
                  enableDensityToggle={false}
                  enableFullScreenToggle={false}
                  enableColumnActions={false}
                  enableSorting={false}
                  enableColumnFilters={false}
                  muiTableContainerProps={{
                    sx: { maxHeight: "100%" },
                  }}
                  muiTableHeadCellProps={{
                    sx: {
                      fontWeight: 600,
                      backgroundColor: "var(--color-container-background)",
                      fontSize: 12,
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <Box
        sx={{
          p: 2,
          borderTop: "1px solid #eee",
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Button
          variant="contained"
          sx={{
            borderRadius: 2,
            minWidth: 120,
            fontSize: 14,
            py: 1.2,
            textTransform: "none",
            color: "var(--color-primary)",
            height: 35,
            background: "var(--color-page-background)",
            border: "1px solid #00b59e",
            "&:hover": { backgroundColor: "var(--color-container-background)" },
          }}
          onClick={handlePrevStep}
          disabled={step === 0}
        >
          Back
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            sx={{
              borderRadius: 2,
              minWidth: 100,
              fontSize: 14,
              py: 1.2,
              textTransform: "none",
              color: "var(--color-primary)",
              height: 35,
              background: "var(--color-page-background)",
              border: "1px solid #00b59e",
              "&:hover": { backgroundColor: "var(--color-container-background)" },
            }}
            onClick={onClose}
          >
            {step === 3 ? "Done" : "Cancel"}
          </Button>
          {step === 0 && (
            <Button
              variant="contained"
              onClick={handleNextStep}
              disabled={!basiqUserId}
              sx={{
                borderRadius: 2,
                minWidth: 100,
                fontSize: 14,
                py: 1.2,
                textTransform: "none",
                color: "#fff",
                height: 35,
                backgroundColor: "var(--color-primary)",
                "&:hover": { backgroundColor: "var(--color-primary)" },
                "&:disabled": { opacity: 0.5, backgroundColor: "#ccc" },
              }}
            >
              Next
            </Button>
          )}
          {step === 1 && (
            <>
              <Button
                variant="contained"
                onClick={handleOpenConsentUI}
                sx={{
                  borderRadius: 2,
                  minWidth: 150,
                  fontSize: 14,
                  py: 1.2,
                  textTransform: "none",
                  color: "var(--color-primary)",
                  height: 35,
                  background: "var(--color-page-background)",
                  border: "1px solid #00b59e",
                  "&:hover": { backgroundColor: "var(--color-container-background)" },
                }}
              >
                Open Consent UI ↗
              </Button>
              <Button
                variant="contained"
                onClick={handleNextStep}
                sx={{
                  borderRadius: 2,
                  minWidth: 100,
                  fontSize: 14,
                  py: 1.2,
                  textTransform: "none",
                  color: "#fff",
                  height: 35,
                  backgroundColor: "var(--color-primary)",
                  "&:hover": { backgroundColor: "var(--color-primary)" },
                }}
              >
                Next
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button
                variant="contained"
                onClick={handlePullTransactions}
                disabled={pullTransactions.isLoading}
                sx={{
                  borderRadius: 2,
                  minWidth: 140,
                  fontSize: 14,
                  py: 1.2,
                  textTransform: "none",
                  color: "#fff",
                  height: 35,
                  backgroundColor: "var(--color-primary)",
                  "&:hover": { backgroundColor: "var(--color-primary)" },
                  "&:disabled": { opacity: 0.5, backgroundColor: "#ccc" },
                }}
              >
                {pullTransactions.isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Pull Transactions"
                )}
              </Button>
              {pulledTransactions.length > 0 && (
                <Button
                  variant="contained"
                  onClick={handleNextStep}
                  sx={{
                    borderRadius: 2,
                    minWidth: 120,
                    fontSize: 14,
                    py: 1.2,
                    textTransform: "none",
                    color: "#fff",
                    height: 35,
                    backgroundColor: "var(--color-primary)",
                    "&:hover": { backgroundColor: "var(--color-primary)" },
                  }}
                >
                  Review
                </Button>
              )}
            </>
          )}
          {step === 3 && (
            <Button
              variant="contained"
              onClick={handleConfirmImport}
              sx={{
                borderRadius: 2,
                minWidth: 120,
                fontSize: 14,
                py: 1.2,
                textTransform: "none",
                color: "#fff",
                height: 35,
                backgroundColor: "var(--color-primary)",
                "&:hover": { backgroundColor: "var(--color-primary)" },
              }}
            >
              Confirm Import
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default BasiqImportPopup;
